/**
 * `/`: signed out, Runebound Play's landing in Poroboard's look (runebound-app `poroboard/builder/BoardLanding`):
 * the pitch over banner art with the way in, a window onto the table next to the sign-in panel, and what it offers;
 * signed in, the Play hub.
 */
import { Frame, FeatureCard, GhostLink, Hero, LOOK, PoroIcon, PrimaryButton, TABLE_BG, Zone } from '@play/ui';
import { ExternalLink, Loader2, LogIn, ShieldCheck, Swords, Users } from 'lucide-react';
import { useEffect } from 'react';
import { useAuth } from '../auth/auth';
import { openSignIn } from '../auth/SignInDialog';
import { SignInPanel } from '../auth/SignInPanel';
import { RUNEBOUND_URL } from '../components/site';
import { HeroFan } from '../components/HeroFan';
import { useCatalog } from '../lib/catalog';
import { cardFace, faceSources } from '../lib/cards';
import { PlayHub } from './Play';

/** The card whose art sits behind the hero (Poroboard's banner card). */
const HERO_CARD = 'jinx-loose-cannon';

const FEATURES = [
  {
    icon: <Swords size={17} />,
    title: 'Two rules tiers',
    text: 'Barebones: the playmat, you run the game. Recommendation: the table plus a rules advisor.',
  },
  {
    icon: <Users size={17} />,
    title: 'Friends and rooms',
    text: 'Invite friends you follow each other with, or share a room link.',
  },
  {
    icon: <ShieldCheck size={17} />,
    title: 'Fair by design',
    text: 'The server keeps hands and decks hidden. Every action is logged and replayable.',
  },
  {
    icon: <PoroIcon size={17} />,
    title: 'Porodigy at the table',
    text: 'Ask rules questions about your own board during a match.',
  },
];

function useHeroArt(): string | null {
  const catalog = useCatalog();
  if (!catalog) return null;
  const face = cardFace(catalog, { cardId: HERO_CARD, printingId: null });
  // (Riot's CDN, the last source: the mirror may not hold every printing yet)
  return faceSources(face, 744).at(-1) ?? null;
}

function focusSignIn() {
  const panel = document.getElementById('sign-in');
  if (!panel) return;
  panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
  panel.querySelector('input')?.focus({ preventScroll: true });
}

/** The landing page. */
export function HomePage() {
  const { user, loading } = useAuth();
  const art = useHeroArt();
  useEffect(() => {
    if (!loading && !user && window.location.hash === '#sign-in') focusSignIn();
  }, [loading, user]);
  if (loading) {
    return (
      <div className="flex justify-center py-24" style={{ color: LOOK.muted }} role="status" aria-label="Loading">
        <Loader2 className="animate-spin motion-reduce:animate-none" />
      </div>
    );
  }
  if (user) return <PlayHub />;
  return (
    <div className="relative">
      {/* (the hero bleeds 32px past the column; phones have a 16px gutter) */}
      <div className="mx-4 sm:mx-0">
        <Hero
          eyebrow="Runebound Play"
          eyebrowIcon={<Swords size={11} />}
          title="Play Riftbound with your Runebound decks."
          art={art}
          actions={
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2.5">
                <PrimaryButton big onClick={openSignIn}>
                  <LogIn size={17} /> Sign in to play
                </PrimaryButton>
                <GhostLink big href={`${RUNEBOUND_URL}/decks/build`} target="_blank" rel="noreferrer">
                  <ExternalLink size={16} color={LOOK.gold} /> Build a deck
                </GhostLink>
              </div>
              <p className="text-[12.5px]" style={{ color: LOOK.muted }}>
                Free, with your runebound.gg account. 1v1 Standard, best of one.
              </p>
            </div>
          }
        >
          A shared digital table for 1v1 Standard. Pick a deck you built on runebound.gg, then play a friend, the casual
          queue or a practice bot.
        </Hero>
      </div>

      <div className="mt-2 grid lg:grid-cols-[minmax(0,1fr)_400px] gap-4 items-stretch">
        <Frame
          title="The table"
          icon={<Swords size={15} color="#b9adff" />}
          end={
            <span className="text-[13px] hidden sm:inline" style={{ color: LOOK.muted }}>
              Your legend, your deck, your opponent across the mat
            </span>
          }
          className="hidden lg:flex flex-col"
        >
          <div
            className="flex-1 min-h-[400px] p-4 grid grid-rows-[56px_minmax(0,1fr)_56px] gap-3"
            style={{ background: TABLE_BG }}
            aria-hidden
          >
            <Zone side="opponent" label="Opponent" slim />
            <div className="grid grid-cols-[minmax(0,1fr)_360px_minmax(0,1fr)] gap-3 min-h-0">
              <Zone label="Battlefield 1" empty />
              <div className="grid place-items-center">
                <HeroFan />
              </div>
              <Zone label="Battlefield 2" empty />
            </div>
            <Zone side="you" label="You" slim />
          </div>
        </Frame>
        <SignInPanel />
      </div>

      <ul className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" aria-label="What you get">
        {FEATURES.map((f) => (
          <li key={f.title} className="contents">
            <FeatureCard icon={f.icon} title={f.title}>
              {f.text}
            </FeatureCard>
          </li>
        ))}
      </ul>
    </div>
  );
}
